import { FC } from 'react';

import { Errors, HeaderText, Loader } from '@/components';
import { findApplicableErrorDetails } from '@/components/common/Errors/utils';
import { HeaderSize } from '@/components/ui/HeaderText';

import { useErrorDirectives } from '@/context/ErrorDirectiveContext';

import { ERROR_KINDS } from '@/core/constants';
import { TypeProduct } from '@/core/generated-types/TypeProduct';

import useCustomerOrders from '@/lib/api/orderManager/hooks/useCustomerOrders';
import useDraftOrders from '@/lib/api/orderManager/hooks/useDraftOrders';

import { TypeDecoratedSubEngineSubscription } from '@/lib/api/subscriptionManager/decorators/subEngine/decorateSubEngineSubscriptions';

import { TypeAccountERCTranslations } from '@/utils/translations/types/TypeAccountERCTranslations';

import { Box } from '../components/Box';

import { getErcBoxes } from '../utils';

export type LayoutPausedErcSubscriptionProps = {
  t: TypeAccountERCTranslations;
  selectedSubscription: TypeDecoratedSubEngineSubscription;
  contentfulProductsObject: Record<string, TypeProduct>;
};

const LayoutPausedErcSubscription: FC<LayoutPausedErcSubscriptionProps> = ({
  t,
  selectedSubscription,
  contentfulProductsObject,
}) => {
  const { errorDirectives } = useErrorDirectives();

  const { errors, heading } = findApplicableErrorDetails(
    errorDirectives,
    ERROR_KINDS.DEFAULT,
  );

  const { customerOrdersData, customerOrdersLoading, customerOrdersError } =
    useCustomerOrders(selectedSubscription.user_uuid);

  const { draftOrdersData, draftOrdersLoading, draftOrdersError } =
    useDraftOrders(selectedSubscription.user_uuid, selectedSubscription.id);

  if (customerOrdersLoading || draftOrdersLoading) {
    return (
      <div data-testid="layout-Paused-subscription">
        <Loader loading text={t.loading} />
      </div>
    );
  }

  if (customerOrdersError || draftOrdersError) {
    return (
      <div data-testid="layout-Paused-subscription">
        <Errors errors={errors} heading={heading} />
      </div>
    );
  }

  const orders = customerOrdersData?.orders;

  const draftOrder = draftOrdersData?.draft_orders[0];

  const { currentBox, pausedBox } = getErcBoxes({
    subscription: selectedSubscription,
    orders,
    draftOrder,
    contentfulProductsObject,
  });

  return (
    <div className="grid gap-y-10" data-testid="layout-Paused-subscription">
      {/* Current Box */}
      <div className="grid gap-y-2 md:gap-y-4">
        <HeaderText
          classes="px-6 xl:px-0"
          size={HeaderSize.H1}
          text={t.currentBox}
        />

        {currentBox.orderData && (
          <Box
            t={t}
            orderData={currentBox.orderData}
            installmentData={currentBox.installmentData}
            productData={currentBox.productData}
            isCurrentBox
          />
        )}
      </div>

      {/* Paused Box */}
      <div className="grid gap-y-2 md:gap-y-4">
        <HeaderText
          classes="px-6 xl:px-0"
          size={HeaderSize.H1}
          text={t.pausedBox}
        />

        {pausedBox.orderData && (
          <Box
            t={t}
            orderData={pausedBox.orderData}
            installmentData={pausedBox.installmentData}
            productData={pausedBox.productData}
            paymentMethodData={pausedBox.paymentMethodData}
            isPausedBox
          />
        )}
      </div>
    </div>
  );
};

export default LayoutPausedErcSubscription;
